import { ReactNode, useMemo } from 'react';

import {
  RbacOrganizationApiContext,
  RbacOrganizationApiProvider,
} from '@gasbi/shared/web/RbacOrganization';

import {
  addressesApi,
  useGetBuildingNumbersQuery,
  useGetSettlementsIInfiniteQuery,
  useGetStreetIInfiniteQuery,
} from '@/features/addresses/addressesApi';
import {
  useGetDepartmentsInfiniteQuery,
  useGetDepartmentsQuery,
} from '@/features/departments/departmentsApi';
import {
  useActivateEmployeeMutation,
  useCreateEmployeeMutation,
  useDeactivateEmployeeMutation,
  useGetEmployeeByIdQuery,
  useGetEmployeesInfiniteQuery,
  useLazyGetEmployeeByEmailQuery,
  useRemovePasswordMutation,
  useSendActivationRequestMutation,
  useUpdateEmployeeMutation,
} from '@/features/employees/employeesApi';
import {
  useGetOrganizationByIdQuery,
  useGetOrganizationLogoQuery,
  useUpdateOrganizationMutation,
} from '@/features/organization/organizationApi';
import { useGetOrganizationBankAccountsInfiniteQuery } from '@/features/organizationBankAccounts/organizationBankAccountsApi';
import {
  useGetOrganizationRolesInfiniteQuery,
  useGetRolesForFilterQuery,
} from '@/features/organizations/organizationsApi';
import {
  useGetPositionsInfiniteQuery,
  useGetPositionsQuery,
} from '@/features/positions/positionsApi';
import { validateCodeFn } from '@/forms/utils/validateCodeFn';

interface Props {
  children: ReactNode;
}

export const SupplyOrganizationApiWrapper = ({ children }: Props) => {
  const value = useMemo<RbacOrganizationApiContext>(
    () => ({
      organizationApi: {
        useGetOrganizationByIdQuery,
        useGetOrganizationLogoQuery,
        useUpdateOrganizationMutation,
      },
      employeesApi: {
        useGetEmployeesInfiniteQuery,
        useGetEmployeeByIdQuery,
        useLazyGetEmployeeByEmailQuery,
        useCreateEmployeeMutation,
        useUpdateEmployeeMutation,
        useSendActivationRequestMutation,
        useRemovePasswordMutation,
        useDeactivateEmployeeMutation,
        useActivateEmployeeMutation,
      },
      departmentsApi: {
        useGetDepartmentsQuery,
        useGetDepartmentsInfiniteQuery,
      },
      positionsApi: {
        useGetPositionsQuery,
        useGetPositionsInfiniteQuery,
      },
      rolesApi: {
        useGetRolesForFilterQuery,
        useGetOrganizationRolesInfiniteQuery,
      },
      bankAccountsApi: {
        useGetOrganizationBankAccountsInfiniteQuery,
      },
      addressesApi: {
        addressesApi,
        useGetBuildingNumbersQuery,
        useGetSettlementsIInfiniteQuery,
        useGetStreetIInfiniteQuery,
      },
      validateCodeFn,
    }),
    [],
  );

  return (
    <RbacOrganizationApiProvider value={value}>
      {children}
    </RbacOrganizationApiProvider>
  );
};
